import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { CaretRight, ArrowRight, Check, X, MagnifyingGlass } from '@phosphor-icons/react';
import { useMutation } from '@apollo/client';
import { getAllCustomers, addCustomer } from './graphql/services/customerService';
import { getSalonServicesGroupedByCategory } from './graphql/services/salonService';
import { getAllPackages, SalonPackage } from './graphql/services/packages.service';
import { GroupedService, SalonService as ApiSalonService } from './graphql/services/salonService';
import { WebCheckinInput } from './graphql/services/customerService';
import { WEB_CHECKIN } from './graphql/queries/WebCheckInQueries';
import CheckinSuccess from './CheckinSuccess';
import OTPVerificationModal, { OTPVerificationHandle } from './components/OTPVerificationModal';
import { useParams } from 'react-router-dom';
import { getSalonById } from './graphql/services/salon.service';
import { authService } from './graphql/services/authServices';
import { getViewURL } from './graphql/services/file.service';

interface Service {
  id: string;
  name: string;
  price: number;
}

const BookService: React.FC = () => {
  const { salonId } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: ''
  });
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [serviceCategories, setServiceCategories] = useState<GroupedService[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [salonName, setSalonName] = useState<string>('');
  const [salonLogo, setSalonLogo] = useState<string>('');
  const [packages, setPackages] = useState<SalonPackage[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<SalonPackage[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState('');
  const [toasterType, setToasterType] = useState<'success' | 'error'>('success');
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // OTP Modal ref and state
  const otpModalRef = useRef<OTPVerificationHandle>(null);
  const [otpRemainingTime, setOtpRemainingTime] = useState(0);
  const [isOtpResendDisabled, setIsOtpResendDisabled] = useState(false);
  const [otpTransactionId, setOtpTransactionId] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSuccess, setOtpSuccess] = useState<string | null>(null);

  const OTP_ENTITY_TYPE = 'customer';
  const OTP_TRANSACTION_TYPE = 'verify_phone';

  // Ref debug removed

  // Web check-in mutation
  const [webCheckin] = useMutation(WEB_CHECKIN);

  // Load services from API
  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      const effectiveSalonId = salonId || '81';
      const [services, pkgRes] = await Promise.all([
        getSalonServicesGroupedByCategory(effectiveSalonId),
        getAllPackages(effectiveSalonId),
      ]);
      setServiceCategories(services);
      setPackages(pkgRes?.packages || []);
    } catch (error: any) {
      setErrorMessage('Failed to load services. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  // Load services on first load and when salon changes
  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // Load salon details for title and logo
  useEffect(() => {
    const fetchSalon = async () => {
      try {
        const effectiveSalonId = salonId || '81';
        const salon = await getSalonById(effectiveSalonId);
        if (salon?.salon_name) {
          setSalonName(salon.salon_name);
          
          // If salon logo exists, get the view URL
          if (salon.salon_logo) {
            try {
              const logoResponse = await getViewURL(salon.salon_logo);
              if (logoResponse?.success && logoResponse?.viewURL) {
                setSalonLogo(logoResponse.viewURL);
              } else {
                setSalonLogo('');
              }
            } catch (logoError) {
              console.error('Error fetching logo view URL:', logoError);
              setSalonLogo('');
            }
          } else {
            setSalonLogo('');
          }
        } else {
          setSalonName('');
          setSalonLogo('');
        }
      } catch (e) {
        setSalonName('');
        setSalonLogo('');
      }
    };
    fetchSalon();
  }, [salonId]);


  // Filter services based on search query
  const filteredServiceCategories = serviceCategories.map(categoryGroup => ({
    ...categoryGroup,
    services: categoryGroup.services.filter(salonService => 
      salonService.service.service_name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(categoryGroup => categoryGroup.services.length > 0);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'mobileNumber') {
      // Only allow digits and limit to 10 characters
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setFormData(prev => ({ ...prev, [field]: numericValue }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const showToasterMessage = (message: string, type: 'success' | 'error' = 'success') => {
    setToasterMessage(message);
    setToasterType(type);
    setShowToaster(true);
    setTimeout(() => {
      setShowToaster(false);
    }, 5000);
  };

  // Helper function to check if a service exists in any selected package
  const isServiceInPackages = (serviceId: string): boolean => {
    return selectedPackages.some(pkg => 
      pkg.salon_services?.some(service => service.id === serviceId)
    );
  };

  // Helper function to check if any services in a package are already selected individually
  const hasPackageServicesSelected = (pkg: SalonPackage): boolean => {
    return pkg.salon_services?.some(service => 
      selectedServices.some(selectedService => selectedService.id === service.id)
    ) || false;
  };

  const handleServiceSelect = (salonService: ApiSalonService) => {
    const service: Service = {
      id: salonService.id,
      name: salonService.description || `Service ${salonService.service.id}`,
      price: salonService.price
    };

    // Check if service is already selected
    const isSelected = selectedServices.find(s => s.id === service.id);
    
    if (isSelected) {
      // Remove if already selected
      const newServices = selectedServices.filter(s => s.id !== service.id);
      setSelectedServices(newServices);
      
      const remainingCount = newServices.length;
      if (remainingCount === 0) {
        showToasterMessage(`Service removed`);
      } else if (remainingCount === 1) {
        showToasterMessage(`1 service selected`);
      } else {
        showToasterMessage(`${remainingCount} services selected`);
      }
    } else {
      // Check if service already exists in any selected package
      if (isServiceInPackages(service.id)) {
        showToasterMessage(`This service already exists in the selected packages`, 'error');
        return;
      }

      // Add service
      const newServices = [...selectedServices, service];
      setSelectedServices(newServices);
      
      const totalCount = newServices.length;
      if (totalCount === 1) {
        showToasterMessage("1 service selected");
      } else {
        showToasterMessage(`${totalCount} services selected`);
      }
    }
    setErrorMessage('');
  };

  const handlePackageSelect = (pkg: SalonPackage) => {
    // Check if package is already selected
    const isSelected = selectedPackages.find(p => p.id === pkg.id);
    
    if (isSelected) {
      // Remove if already selected
      const newPackages = selectedPackages.filter(p => p.id !== pkg.id);
      setSelectedPackages(newPackages);
      
      // Always show "Package removed" when deselecting
      showToasterMessage(`Package removed`);
    } else {
              // Check if any services in this package are already selected individually
        if (hasPackageServicesSelected(pkg)) {
          showToasterMessage(`One or more services that belong to this package have been already selected`, 'error');
          return;
        }

      // Add package
      const newPackages = [...selectedPackages, pkg];
      setSelectedPackages(newPackages);
      
      if (newPackages.length === 1) {
        showToasterMessage("1 package selected");
      } else {
        showToasterMessage(`${newPackages.length} packages selected`);
      }
    }
    setErrorMessage('');
  };

  const isFormValid = formData.mobileNumber.length === 10 && (selectedServices.length > 0 || selectedPackages.length > 0);

  const getSelectedItemsText = () => {
    const totalSelected = selectedServices.length + selectedPackages.length;
    if (totalSelected > 0) {
      return `${totalSelected} item${totalSelected > 1 ? 's' : ''} selected`;
    }
    return 'Choose a service or package from the list';
  };

  const performWebCheckin = async () => {
    try {
      setCheckinLoading(true);
      
      // Collect salon service IDs and package IDs separately
      const salonServiceIds = selectedServices.map(service => parseInt(service.id));
      const packageIds = selectedPackages.map(pkg => parseInt(pkg.id));
      
      const input: WebCheckinInput = {
        salon_id: parseInt(salonId || '81', 10),
        customer_name: formData.name,
        customer_mobile_number: formData.mobileNumber,
        salon_service_ids: salonServiceIds,
        package_ids: packageIds
      };

      const response = await webCheckin({ variables: { input } });

      if (response.data?.webCheckin?.success) {
        setFormData({ name: '', mobileNumber: '' });
        setSelectedServices([]);
        setSelectedPackages([]);
        setShowSuccessPage(true);
      } else {
        showToasterMessage('Check-in failed. Please try again.', 'error');
      }
    } catch (error: any) {
      const message = error?.graphQLErrors?.[0]?.message || error?.message || 'Check-in failed. Please try again.';
      showToasterMessage(message, 'error');
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!isFormValid) return;

    try {
      setCheckinLoading(true);
      // 1) Check if customer exists by phone
      const customersResponse = await getAllCustomers({ search: formData.mobileNumber });
      const customers = customersResponse?.getAllCustomers?.customers || [];
      const exists = customers.some(c => (c.phone || '').endsWith(formData.mobileNumber));

      if (exists) {
        await performWebCheckin();
        return;
      }

      // 2) If not exists, send OTP and open modal
      const sendResult = await authService.sendOTP(formData.mobileNumber, OTP_ENTITY_TYPE, OTP_TRANSACTION_TYPE);
      const transactionId = sendResult?.sendOTP?.transactionId;
      if (!transactionId) {
        throw new Error('Failed to initiate OTP. Please try again.');
      }
      setOtpTransactionId(transactionId);
      setOtpError(null);
      setOtpSuccess('OTP sent successfully');
      otpModalRef.current?.show();

      // Start countdown timer
      setIsOtpResendDisabled(true);
      setOtpRemainingTime(30);
      const timer = setInterval(() => {
        setOtpRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsOtpResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      const message = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to process. Please try again.';
      showToasterMessage(message, 'error');
    } finally {
      setCheckinLoading(false);
    }
  };

  // OTP Modal handlers
  const handleOTPVerify = async (otp: string) => {
    if (!otpTransactionId) {
      setOtpError('Session expired. Please resend OTP.');
      return;
    }
    try {
      setOtpError(null);
      const res = await authService.verifyPhoneOTP(
        formData.mobileNumber,
        otp,
        otpTransactionId,
        OTP_ENTITY_TYPE,
      );
      if (res?.verifyPhoneOTP?.success) {
        setOtpSuccess('Phone verified successfully');
        otpModalRef.current?.hide();
        // Create customer after successful OTP verification
        try {
          const addResult = await addCustomer({
            name: formData.name || '',
            phone: formData.mobileNumber,
            signup_source: 'web',
            signup_salon_id: salonId || '81',
          });
          if (!addResult?.addCustomer?.success) {
            const msg = addResult?.addCustomer?.message || 'Failed to create customer';
            showToasterMessage(msg, 'error');
            setOtpError(msg);
            return;
          }
        } catch (addErr: any) {
          const msg = addErr?.message || 'Failed to create customer';
          showToasterMessage(msg, 'error');
          setOtpError(msg);
          return;
        }
        await performWebCheckin();
      } else {
        setOtpError(res?.verifyPhoneOTP?.message || 'Invalid OTP. Please try again.');
      }
    } catch (error: any) {
      const message = error?.graphQLErrors?.[0]?.message || error?.message || 'OTP verification failed.';
      setOtpError(message);
    }
  };

  const handleOTPResend = async () => {
    try {
      setOtpError(null);
      const res = await authService.resendOTP(
        formData.mobileNumber,
        OTP_ENTITY_TYPE,
        OTP_TRANSACTION_TYPE,
      );
      const newTxnId = res?.resendOTP?.transactionId;
      if (newTxnId) {
        setOtpTransactionId(newTxnId);
      }
      setOtpSuccess('OTP resent successfully');

      // Start countdown timer
      setIsOtpResendDisabled(true);
      setOtpRemainingTime(30);
      const timer = setInterval(() => {
        setOtpRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsOtpResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      const message = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to resend OTP.';
      setOtpError(message);
    }
  };

  return (
    <Container>
      {salonName && (
        <SalonHeader>
          <SalonBox>
            <LeftDiamond />
            <RightDiamond />
            {salonLogo ? (
              <SalonIcon className="salon-icon">
                <img 
                  src={salonLogo} 
                  alt="Salon Logo" 
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </SalonIcon>
            ) : null}
            <SalonName>{salonName}</SalonName>
          </SalonBox>
        </SalonHeader>
      )}
      <Content>
        <PageTitle>Book a Service</PageTitle>
        
        <Form>
          <InputGroup>
            <Label>Your Name</Label>
            <Input
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
            />
          </InputGroup>

          <InputGroup>
            <Label>Mobile Number *</Label>
            <Input
              type="tel"
              placeholder="Enter your 10-digit number"
              value={formData.mobileNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('mobileNumber', e.target.value)}
              maxLength={10}
            />
          </InputGroup>

          <InputGroup>
            <Label>Select service *</Label>
            <ServiceSelector onClick={() => setShowModal(true)}>
              <span style={{ color: selectedServices.length > 0 ? '#333' : '#A9A3A3' }}>
                {getSelectedItemsText()}
              </span>
              <CaretRight size={20} color="#545454" />
            </ServiceSelector>
          </InputGroup>

         

          <CheckInButton 
            type="button"
            disabled={!isFormValid || checkinLoading}
            onClick={handleCheckIn}
          >
            {checkinLoading ? 'Processing...' : 'Check-In'} 
            <ArrowRight size={20} color="#fff" />
          </CheckInButton>
        </Form>
      </Content>

      {showModal && (
        <ModalOverlay onClick={() => {
          setShowModal(false);
        }}>
          <Modal onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}>
            <ModalHeader>
              <ModalTitleContainer>
                <ModalTitle>Select service</ModalTitle>
              </ModalTitleContainer>
              <ModalIcons>
                {(selectedServices.length > 0 || selectedPackages.length > 0) && (
                  <TickButton onClick={() => setShowModal(false)}>
                    <Check size={20} color="#B02E2F" />
                  </TickButton>
                )}
                <CloseButton onClick={() => setShowModal(false)}>
                  <X size={20} color="#666" />
                </CloseButton>
              </ModalIcons>
            </ModalHeader>

            <FixedSection>
              <SearchBar>
                <SearchIcon><MagnifyingGlass size={20} color="#333" /></SearchIcon>
                <SearchInput 
                  placeholder="Search services" 
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                />
              </SearchBar>
            </FixedSection>

            <ScrollableServiceList>
              {loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>Loading services...</div>
              ) : filteredServiceCategories.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  {searchQuery ? 'No services found matching your search.' : 'No services available.'}
                </div>
              ) : (
                <>
                  {packages.length > 0 && (
                    <div>
                      <SectionHeader>
                        <SectionTitle>Packages</SectionTitle>
                        <SectionCount>{packages.length}</SectionCount>
                      </SectionHeader>
                      <ServicesContainer>
                        {packages.map((pkg) => (
                          <PackageItem 
                            key={pkg.id} 
                            selected={selectedPackages.some(p => p.id === pkg.id)}
                            onClick={() => handlePackageSelect(pkg)}
                          >
                            <PackageServices>
                              {pkg.salon_services?.map((service, index) => (
                                <React.Fragment key={service.service.id}>
                                  <span>{service.service.service_name}</span>
                                  {index < (pkg.salon_services?.length || 0) - 1 && (
                                    <PlusIcon>+</PlusIcon>
                                  )}
                                </React.Fragment>
                              ))}
                            </PackageServices>
                            <PackagePrice>₹{pkg.salon_services?.reduce((total, service) => total + service.price, 0) || 0}</PackagePrice>
                          </PackageItem>
                        ))}
                      </ServicesContainer>
                    </div>
                  )}

                  {filteredServiceCategories.map((categoryGroup) => (
                    <div key={categoryGroup.category.id}>
                      <SectionHeader>
                        <SectionTitle>{categoryGroup.category.category_name}</SectionTitle>
                        <SectionCount>{categoryGroup.services.length}</SectionCount>
                      </SectionHeader>
                      
                      <ServicesContainer>
                        {categoryGroup.services.map((salonService) => (
                          <ServiceItem
                            key={salonService.id}
                            selected={selectedServices.some(s => s.id === salonService.id)}
                            onClick={() => handleServiceSelect(salonService)}
                          >
                            <ServiceName>
                              {salonService.service.service_name}
                            </ServiceName>
                            <ServicePrice>₹{salonService.price}</ServicePrice>
                          </ServiceItem>
                        ))}
                      </ServicesContainer>
                    </div>
                  ))}
                </>
              )}
            </ScrollableServiceList>

            {errorMessage && (
              <ErrorMessage>
                <ErrorIcon>⚠</ErrorIcon>
                {errorMessage}
              </ErrorMessage>
            )}
          </Modal>
        </ModalOverlay>
      )}

      {showToaster && (
        <Toaster type={toasterType}>
          <ToasterContent type={toasterType}>
            <ToasterIcon>✓</ToasterIcon>
            {toasterMessage}
          </ToasterContent>
        </Toaster>
      )}

      {showSuccessPage && (
        <CheckinSuccess onClose={() => setShowSuccessPage(false)} />
      )}

      {/* OTP Verification Modal */}
      <OTPVerificationModal
        ref={otpModalRef}
        phoneNumber={formData.mobileNumber || '+91 98765 43210'}
        onVerify={handleOTPVerify}
        onResend={handleOTPResend}
        remainingTime={otpRemainingTime}
        isResendDisabled={isOtpResendDisabled}
        error={otpError}
        successMessage={otpSuccess}
        onError={setOtpError}
        onSuccess={setOtpSuccess}
        onClose={() => {
          console.log('OTP modal closed');
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: transparent;
  font-family: 'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const SalonHeader = styled.div`
  background: transparent;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 60px;
  position: relative;
`;

const SalonBox = styled.div`
  background: #F7EAEA;
  padding: 12px 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #F3D7D7;
  position: relative;
  z-index: 2;
  max-width: 280px;
  flex-wrap: wrap;
  
  /* Left connecting line */
  &::before {
    content: '';
    position: absolute;
    left: -50vw;
    top: 50%;
    transform: translateY(-50%);
    width: 50vw;
    height: 1px;
    background: #DBDBDB;
  }
  
  /* Right connecting line */
  &::after {
    content: '';
    position: absolute;
    right: -50vw;
    top: 50%;
    transform: translateY(-50%);
    width: 50vw;
    height: 1px;
    background: #DBDBDB;
  }
`;

const LeftDiamond = styled.div`
  position: absolute;
  left: -10px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
  width: 8px;
  height: 8px;
  background: #DBDBDB;
  z-index: 3;
`;

const RightDiamond = styled.div`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
  width: 8px;
  height: 8px;
  background: #DBDBDB;
  z-index: 3;
`;

const SalonIcon = styled.div`
  width: 24px;
  height: 24px;
  aspect-ratio: 1/1;
  border-radius: 48px;
  background: lightgray -1.315px 0px / 109.932% 100% no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    border-radius: 48px;
    object-fit: cover;
  }
`;

const SalonName = styled.h1`
  color: #B02E2F;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  line-height: 1.3;
  word-wrap: break-word;
  hyphens: auto;
  flex: 1;
  min-width: 0;
`;

const Content = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow-y: auto;
  min-height: 0;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 24px 0;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #DBDBDB;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  margin-bottom: 20px;
  &:focus {
    border-color: #B02E2F;
  }
  
  &::placeholder {
    color: #A9A3A3;
  }
`;

const ServiceSelector = styled.div`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: white;
  
  &:hover {
    border-color: #B02E2F;
  }
`;

const CheckInButton = styled.button`
  margin-top: auto;
  padding: 16px;
  background: ${props => props.disabled ? '#ccc' : '#B02E2F'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    background: #B02E2F;
  }
  
  &:active:not(:disabled) {
    background: #8A2527;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px 20px 0 20px;
  flex-shrink: 0;
`;

const ModalTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 500;
  margin: 0;
`;


const ModalIcons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TickButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px 12px;
  margin-left: 16px;
  margin-right: 16px;
  background: white;
`;

const SearchIcon = styled.span`
  margin-right: 8px;
  font-size: 16px;
  color: #333;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
  background: transparent;
  color: #333;
  
  &::placeholder {
    color: #A9A3A3;
  }
`;

const FixedSection = styled.div`
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  padding-bottom: 16px;
  margin: 0 -20px;
  padding-left: 20px;
  padding-right: 20px;
`;

const ScrollableServiceList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: transparent;
  margin-bottom: 8px;
`;

const SectionTitle = styled.span<{ disabled?: boolean }>`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.disabled ? '#A7A7A7' : '#333'};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionCount = styled.span`
  background: transparent;
  color: #666;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  border: none;
`;

const ServicesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 12px;
`;

const PackageItem = styled.div<{ selected: boolean }>`
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${props => props.selected ? '#F7EAEA' : 'white'};
  
  &:hover {
    background: ${props => {
      if (props.selected) return '#F7EAEA';
      return '#f8f8f8';
    }};
  }
`;

const PackageServices = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 14px;
  color: #333;
`;

const PlusIcon = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 600;
`;

const PackagePrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  min-width: 60px;
  text-align: right;
`;

const ServiceItem = styled.div<{ selected: boolean; disabled?: boolean }>`
  padding: 12px 16px;
  border: none;
  border-radius: 0;
  background: ${props => props.selected ? '#F7EAEA' : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
  &:hover {
    background: ${props => {
      if (props.disabled) return 'white';
      if (props.selected) return '#F7EAEA';
      return '#f8f8f8';
    }};
  }
`;

const ServiceName = styled.div<{ disabled?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.disabled ? '#A7A7A7' : '#333'};
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const ServicePrice = styled.div<{ disabled?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.disabled ? '#A7A7A7' : '#000'};
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const ErrorIcon = styled.span`
  font-size: 16px;
`;

const Toaster = styled.div<{ type: 'success' | 'error' }>`
  position: fixed;
  top: 200px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  animation: slideInUp 0.3s ease-out;
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const ToasterContent = styled.div<{ type?: 'success' | 'error' }>`
  background: ${props => props.type === 'error' ? '#F77373' : '#B02E2F'};
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 320px;
  max-width: 90vw;
  white-space: normal;
  line-height: 1.4;
  word-wrap: break-word;
`;

const ToasterIcon = styled.span`
  font-size: 16px;
`;

export default BookService;